import { i18nService } from '../../i18n/I18nService';
import { authService } from '../../auth/AuthService';
import { logout } from '../../api/authApi';
import { fetchProfile, updateProfile, deleteProfile } from '../../api/profileApi';
import { fetchMyBookings, cancelBooking } from '../../api/bookingApi';
import { ApiError } from '../../api/authApi';
import { formatDurationFromMinutes } from './admin/context';
import { createFormField, EMAIL_RE } from '../formField';
function t(key, params) {
    return i18nService.t(key, params);
}
function createHeader() {
    const header = document.createElement('div');
    header.className = 'profile__header';
    const title = document.createElement('h1');
    title.className = 'profile__title';
    title.textContent = t('profile.title');
    header.append(title);
    return header;
}
function createBackButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cta profile__back';
    button.textContent = t('profile.back');
    button.addEventListener('click', () => {
        window.location.hash = '#page=home';
    });
    return button;
}
function openCancelBlockedModal() {
    const overlay = document.createElement('div');
    overlay.className = 'profile__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'profile__modal';
    const body = document.createElement('p');
    body.className = 'profile__modal-text';
    body.textContent = t('profile.bookings.cancelError.tooLate');
    const contactsLabel = document.createElement('p');
    contactsLabel.className = 'profile__modal-contacts-label';
    contactsLabel.textContent = t('booking.insufficientTimeModal.contactsLabel');
    const contacts = document.createElement('div');
    contacts.className = 'profile__modal-contacts';
    const phone1Link = document.createElement('a');
    phone1Link.href = `tel:+35799213755`;
    phone1Link.textContent = `${t('booking.insufficientTimeModal.phone1Name')}: ${t('booking.insufficientTimeModal.phone1')}`;
    const phone1Line = document.createElement('p');
    phone1Line.className = 'profile__modal-contact-line';
    phone1Line.appendChild(phone1Link);
    const phone2Link = document.createElement('a');
    phone2Link.href = `tel:+35796793008`;
    phone2Link.textContent = `${t('booking.insufficientTimeModal.phone2Name')}: ${t('booking.insufficientTimeModal.phone2')}`;
    const phone2Line = document.createElement('p');
    phone2Line.className = 'profile__modal-contact-line';
    phone2Line.appendChild(phone2Link);
    const emailLink = document.createElement('a');
    emailLink.href = `mailto:ladyspace.cyprus@gmail.com`;
    emailLink.textContent = t('booking.insufficientTimeModal.email');
    const emailLine = document.createElement('p');
    emailLine.className = 'profile__modal-contact-line';
    emailLine.appendChild(emailLink);
    contacts.append(phone1Line, phone2Line, emailLine);
    const actions = document.createElement('div');
    actions.className = 'profile__modal-actions';
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'cta';
    closeButton.textContent = t('booking.insufficientTimeModal.closeButton');
    closeButton.addEventListener('click', () => overlay.remove());
    actions.appendChild(closeButton);
    modal.append(body, contactsLabel, contacts, actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}
function openCancelConfirm(booking, onConfirm) {
    const overlay = document.createElement('div');
    overlay.className = 'profile__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'profile__modal profile__modal--confirm';
    const text = document.createElement('p');
    text.className = 'profile__modal-confirm-text';
    text.textContent = t('profile.bookings.cancelConfirm.text');
    const actions = document.createElement('div');
    actions.className = 'profile__modal-confirm-actions';
    const noButton = document.createElement('button');
    noButton.type = 'button';
    noButton.className = 'cta';
    noButton.textContent = t('profile.bookings.cancelConfirm.no');
    noButton.addEventListener('click', () => overlay.remove());
    const yesButton = document.createElement('button');
    yesButton.type = 'button';
    yesButton.className = 'cta profile__submit';
    yesButton.textContent = t('profile.bookings.cancelConfirm.yes');
    yesButton.addEventListener('click', () => {
        yesButton.disabled = true;
        noButton.disabled = true;
        yesButton.classList.add('is-loading');
        cancelBooking({ bookingId: booking.id })
            .then(() => {
            overlay.remove();
            onConfirm();
        })
            .catch((err) => {
            if (err.code === 'too_late') {
                overlay.remove();
                openCancelBlockedModal();
            }
            else {
                console.error('Failed to cancel booking:', err);
                yesButton.disabled = false;
                noButton.disabled = false;
                yesButton.classList.remove('is-loading');
                const msg = err.message || t('profile.bookings.cancelError.generic');
                alert(msg);
            }
        });
    });
    actions.append(noButton, yesButton);
    modal.append(text, actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}
// Mirrors the admin panel's confirm modal (src/render/components/admin/remainingHours.ts):
// an overlay + centered "are you sure" card with Yes/No. Appended straight to
// document.body since profile.ts has no shared modal-host element like
// AdminContext.section.
function openLogoutConfirm() {
    const overlay = document.createElement('div');
    overlay.className = 'profile__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'profile__modal profile__modal--confirm';
    const text = document.createElement('p');
    text.className = 'profile__modal-confirm-text';
    text.textContent = t('profile.logoutConfirm.text');
    const actions = document.createElement('div');
    actions.className = 'profile__modal-confirm-actions';
    const noButton = document.createElement('button');
    noButton.type = 'button';
    noButton.className = 'cta';
    noButton.textContent = t('profile.logoutConfirm.no');
    noButton.addEventListener('click', () => overlay.remove());
    const yesButton = document.createElement('button');
    yesButton.type = 'button';
    yesButton.className = 'cta profile__submit';
    yesButton.textContent = t('profile.logoutConfirm.yes');
    yesButton.addEventListener('click', () => {
        yesButton.disabled = true;
        noButton.disabled = true;
        yesButton.classList.add('is-loading');
        // Revoke the session server-side, but clear the local auth state and
        // navigate home regardless of whether that call succeeds — matching the
        // admin panel's own logout button, which drops to the login screen in a
        // `.finally()` rather than leaving the user stuck if the request fails.
        logout()
            .catch((err) => console.error('Failed to log out:', err))
            .finally(() => {
            authService.clear();
            overlay.remove();
            window.location.hash = '#page=home';
        });
    });
    actions.append(noButton, yesButton);
    modal.append(text, actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}
// Shown after a save that asked for a new email. The address on screen is
// still the old one at this point and stays that way until the link sent to
// the new mailbox is opened, which is the whole reason a typo can't lock the
// account any more — so the copy has to say that, or the unchanged email row
// just looks like the save silently failed.
function openEmailChangePendingNotice(pendingEmail) {
    const overlay = document.createElement('div');
    overlay.className = 'profile__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'profile__modal profile__modal--confirm';
    const title = document.createElement('h2');
    title.className = 'profile__section-title';
    title.textContent = t('profile.edit.emailPendingTitle');
    const text = document.createElement('p');
    text.className = 'profile__modal-confirm-text';
    text.textContent = t('profile.edit.emailPendingText', { email: pendingEmail });
    const actions = document.createElement('div');
    actions.className = 'profile__modal-confirm-actions';
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'cta profile__submit';
    closeButton.textContent = t('profile.edit.emailPendingClose');
    closeButton.addEventListener('click', () => overlay.remove());
    actions.appendChild(closeButton);
    modal.append(title, text, actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}
function openEditProfileModal(profile, onSaved) {
    const overlay = document.createElement('div');
    overlay.className = 'profile__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'profile__modal';
    const title = document.createElement('h2');
    title.className = 'profile__section-title';
    title.textContent = t('profile.edit.title');
    const form = document.createElement('form');
    form.className = 'auth__form';
    const phoneField = createFormField('profile', t('profile.edit.phoneLabel'), 'tel', profile.phone ?? '');
    const emailField = createFormField('profile', t('profile.edit.emailLabel'), 'email', profile.email);
    const error = document.createElement('p');
    error.className = 'auth__error';
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'profile__modal-confirm-actions';
    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'cta';
    cancelButton.textContent = t('profile.edit.cancel');
    cancelButton.addEventListener('click', () => overlay.remove());
    const saveButton = document.createElement('button');
    saveButton.type = 'submit';
    saveButton.className = 'cta profile__submit';
    saveButton.textContent = t('profile.edit.save');
    actions.append(cancelButton, saveButton);
    form.append(phoneField.wrapper, emailField.wrapper, error, actions);
    form.addEventListener('submit', (event) => {
        event.preventDefault();
        error.hidden = true;
        const phone = phoneField.input.value.trim();
        const email = emailField.input.value.trim().toLowerCase();
        if (!phone || !EMAIL_RE.test(email)) {
            error.textContent = t('profile.edit.invalidFieldsError');
            error.hidden = false;
            return;
        }
        saveButton.disabled = true;
        cancelButton.disabled = true;
        saveButton.classList.add('is-loading');
        updateProfile({ email, phone, locale: i18nService.getLocale() })
            .then((updated) => {
            overlay.remove();
            // `updated.email` is whatever the account still uses — the old address
            // when a change is pending — so the profile row stays truthful without
            // any special-casing here.
            onSaved({ email: updated.email, phone: updated.phone });
            if (updated.emailChangePending && updated.pendingEmail) {
                openEmailChangePendingNotice(updated.pendingEmail);
            }
        })
            .catch((err) => {
            saveButton.disabled = false;
            cancelButton.disabled = false;
            saveButton.classList.remove('is-loading');
            error.textContent = err.code === 'email_exists' ? t('profile.edit.emailExistsError') : t('profile.edit.genericError');
            error.hidden = false;
        });
    });
    modal.append(title, form);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}
// Unlike openLogoutConfirm, this must NOT clear auth/redirect on failure:
// logout is best-effort (revoking a session is low-stakes), but deleting a
// profile is destructive and irreversible, so a failed request has to leave
// the account intact and let the user retry — mirrors openCancelConfirm's
// catch shape instead.
function openDeleteProfileConfirm() {
    const overlay = document.createElement('div');
    overlay.className = 'profile__modal-overlay';
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay)
            overlay.remove();
    });
    const modal = document.createElement('div');
    modal.className = 'profile__modal profile__modal--confirm';
    const text = document.createElement('p');
    text.className = 'profile__modal-confirm-text';
    text.textContent = t('profile.deleteConfirm.text');
    const actions = document.createElement('div');
    actions.className = 'profile__modal-confirm-actions';
    const noButton = document.createElement('button');
    noButton.type = 'button';
    noButton.className = 'cta';
    noButton.textContent = t('profile.deleteConfirm.no');
    noButton.addEventListener('click', () => overlay.remove());
    const yesButton = document.createElement('button');
    yesButton.type = 'button';
    yesButton.className = 'cta profile__delete';
    yesButton.textContent = t('profile.deleteConfirm.yes');
    yesButton.addEventListener('click', () => {
        yesButton.disabled = true;
        noButton.disabled = true;
        yesButton.classList.add('is-loading');
        deleteProfile()
            .then(() => {
            authService.clear();
            overlay.remove();
            window.location.hash = '#page=home';
        })
            .catch((err) => {
            console.error('Failed to delete profile:', err);
            yesButton.disabled = false;
            noButton.disabled = false;
            yesButton.classList.remove('is-loading');
            alert(t('profile.deleteError.generic'));
        });
    });
    actions.append(noButton, yesButton);
    modal.append(text, actions);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
}
function createLogoutButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cta profile__logout';
    button.textContent = t('profile.logoutButton');
    button.addEventListener('click', () => openLogoutConfirm());
    return button;
}
function createDeleteProfileButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cta profile__delete-entry';
    button.textContent = t('profile.deleteButton');
    button.addEventListener('click', () => openDeleteProfileConfirm());
    return button;
}
function renderSubscriptions(container, subscriptions) {
    container.innerHTML = '';
    if (subscriptions.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'profile__table-empty';
        empty.textContent = t('profile.subscriptions.empty');
        container.appendChild(empty);
        return;
    }
    const table = document.createElement('table');
    table.className = 'profile__table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const label of [
        t('profile.subscriptions.table.plan'),
        t('profile.subscriptions.table.place'),
        t('profile.subscriptions.table.timeLeft'),
    ]) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    for (const sub of subscriptions) {
        const row = document.createElement('tr');
        const planCell = document.createElement('td');
        planCell.textContent = sub.planName;
        const placeCell = document.createElement('td');
        placeCell.textContent = sub.placeTypeName;
        const timeLeftCell = document.createElement('td');
        timeLeftCell.textContent = formatDurationFromMinutes(sub.leftMinutes);
        row.append(planCell, placeCell, timeLeftCell);
        tbody.appendChild(row);
    }
    table.appendChild(tbody);
    container.appendChild(table);
}
function renderBookings(container, bookings, onBookingsChanged) {
    container.innerHTML = '';
    if (bookings.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'profile__table-empty';
        empty.textContent = t('profile.bookings.empty');
        container.appendChild(empty);
        return;
    }
    const table = document.createElement('table');
    table.className = 'profile__table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const label of [
        t('profile.bookings.table.place'),
        t('profile.bookings.table.date'),
        t('profile.bookings.table.time'),
        t('profile.bookings.table.payment'),
        '',
    ]) {
        const th = document.createElement('th');
        th.textContent = label;
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    const nowSeconds = Math.floor(Date.now() / 1000);
    for (const booking of bookings) {
        const row = document.createElement('tr');
        const placeCell = document.createElement('td');
        placeCell.textContent = booking.placeName;
        const dateCell = document.createElement('td');
        dateCell.textContent = booking.dateLabel;
        const timeCell = document.createElement('td');
        timeCell.textContent = booking.timeRangeLabel;
        const paymentCell = document.createElement('td');
        paymentCell.textContent =
            booking.paymentType === 'subscription'
                ? booking.planName || '—'
                : t('profile.bookings.table.hourly');
        const actionCell = document.createElement('td');
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'profile__booking-cancel-btn';
        cancelBtn.textContent = t('profile.bookings.cancelButton');
        const canCancel = booking.dateStart - nowSeconds >= 86400;
        cancelBtn.addEventListener('click', () => {
            if (!canCancel) {
                openCancelBlockedModal();
            }
            else {
                openCancelConfirm(booking, () => {
                    onBookingsChanged();
                });
            }
        });
        actionCell.appendChild(cancelBtn);
        row.append(placeCell, dateCell, timeCell, paymentCell, actionCell);
        tbody.appendChild(row);
    }
    table.appendChild(tbody);
    container.appendChild(table);
}
export function renderProfile(_block) {
    if (!authService.isAuthorized) {
        window.location.hash = '#page=auth';
        return document.createElement('section');
    }
    const section = document.createElement('section');
    section.className = 'profile';
    section.appendChild(createHeader());
    const status = document.createElement('p');
    status.className = 'profile__status';
    status.textContent = t('profile.loading');
    section.appendChild(status);
    const locale = i18nService.getLocale();
    Promise.all([fetchProfile(), fetchMyBookings(locale)])
        .then(([profile, bookingsRes]) => {
        section.innerHTML = '';
        section.appendChild(createHeader());
        const details = document.createElement('div');
        details.className = 'profile__details';
        const phoneRow = document.createElement('p');
        phoneRow.className = 'profile__detail';
        phoneRow.textContent = `${t('profile.phoneLabel')}: ${profile.phone ?? '—'}`;
        const emailRow = document.createElement('p');
        emailRow.className = 'profile__detail';
        emailRow.textContent = `${t('profile.emailLabel')}: ${profile.email}`;
        details.append(phoneRow, emailRow);
        section.appendChild(details);
        const editButton = document.createElement('button');
        editButton.type = 'button';
        editButton.className = 'profile__edit-btn';
        editButton.textContent = t('profile.edit.button');
        editButton.addEventListener('click', () => {
            openEditProfileModal({ email: profile.email, phone: profile.phone }, (updated) => {
                profile.email = updated.email;
                profile.phone = updated.phone;
                phoneRow.textContent = `${t('profile.phoneLabel')}: ${profile.phone ?? '—'}`;
                emailRow.textContent = `${t('profile.emailLabel')}: ${profile.email}`;
            });
        });
        section.appendChild(editButton);
        const bookingsTitle = document.createElement('h2');
        bookingsTitle.className = 'profile__section-title';
        bookingsTitle.textContent = t('profile.bookings.title');
        section.appendChild(bookingsTitle);
        const bookingsContainer = document.createElement('div');
        bookingsContainer.className = 'profile__bookings';
        section.appendChild(bookingsContainer);
        const subscriptionsTitle = document.createElement('h2');
        subscriptionsTitle.className = 'profile__section-title';
        subscriptionsTitle.textContent = t('profile.subscriptions.title');
        section.appendChild(subscriptionsTitle);
        const subscriptionsContainer = document.createElement('div');
        subscriptionsContainer.className = 'profile__subscriptions';
        section.appendChild(subscriptionsContainer);
        const onBookingsChanged = () => {
            Promise.all([
                fetchMyBookings(i18nService.getLocale()),
                fetchProfile(),
            ])
                .then(([bookingsRes, updatedProfile]) => {
                renderBookings(bookingsContainer, bookingsRes.bookings, onBookingsChanged);
                renderSubscriptions(subscriptionsContainer, updatedProfile.subscriptions);
            })
                .catch((err) => console.error('Failed to refresh after cancellation:', err));
        };
        renderBookings(bookingsContainer, bookingsRes.bookings, onBookingsChanged);
        renderSubscriptions(subscriptionsContainer, profile.subscriptions);
        section.appendChild(createLogoutButton());
        section.appendChild(createDeleteProfileButton());
        section.appendChild(createBackButton());
    })
        .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
            authService.clear();
            window.location.hash = '#page=auth';
            return;
        }
        console.error('Failed to load profile:', err);
        status.textContent = t('profile.loadError');
    });
    return section;
}
